// Por qué se cayó una sesión de WhatsApp no oficial (Evolution / Baileys).
//
// El backend (microservice-conversations) expone `last_disconnect`
// { at, status_code, reason_key, reason_label, severity } en
// GET /conversations/channel-accounts y GET /integrations, y
// `connection_history` (solo admin/supervisor). La clave de i18n es el
// `reason_key`; `reason_label` (español del backend) es solo el respaldo para
// un motivo nuevo que el front todavía no conoce.

/** reason_key → { short: texto corto (rail), long: texto completo, severity }. */
const REASONS = {
  forbidden: {
    short: "Bloqueo",
    long: "Cuenta bloqueada o suspendida por WhatsApp",
    severity: "error",
  },
  logged_out: {
    short: "Sesión cerrada",
    long: "Sesión cerrada desde el teléfono (dispositivo desvinculado)",
    severity: "warning",
  },
  connection_replaced: {
    short: "Otra sesión",
    long: "Otra sesión reemplazó esta conexión",
    severity: "warning",
  },
  bad_session: {
    short: "Sesión dañada",
    long: "Sesión dañada; hay que volver a vincular",
    severity: "warning",
  },
  multidevice_mismatch: {
    short: "Revincular",
    long: "Incompatibilidad multidispositivo; hay que volver a vincular",
    severity: "warning",
  },
  timed_out: {
    short: "Sin respuesta",
    long: "Tiempo de conexión agotado",
    severity: "info",
  },
  connection_closed: {
    short: "Reconectando",
    long: "Conexión cerrada (reintentando)",
    severity: "info",
  },
  restart_required: {
    short: "Reiniciando",
    long: "Reinicio requerido (normal tras vincular)",
    severity: "info",
  },
  unavailable_service: {
    short: "WhatsApp no disponible",
    long: "Servicio de WhatsApp no disponible",
    severity: "info",
  },
  qr_refused: {
    short: "QR vencido",
    long: "Se agotaron los intentos de QR sin vincular",
    severity: "info",
  },
  logged_out_by_operator: {
    short: "Desvinculado",
    long: "Desvinculado desde BearTrack",
    severity: "info",
  },
};

const SEVERITIES = new Set(["error", "warning", "info"]);

/** Clases por gravedad (tokens de estado: funcionan en dark y daltónico). */
export const SEVERITY_TONE = {
  error: {
    chip: "bg-status-error-soft text-status-error",
    dot: "bg-status-error",
    text: "text-status-error",
  },
  warning: {
    chip: "bg-status-warning-soft text-status-warning",
    dot: "bg-status-warning",
    text: "text-status-warning",
  },
  info: {
    chip: "bg-status-info-soft text-status-info",
    dot: "bg-status-info",
    text: "text-status-info",
  },
};

function validDate(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Motivo legible de una desconexión. Las claves `shortKey`/`labelKey` van por
 * t(); `fallbackLabel` ya viene traducido por el backend (español) y se usa
 * cuando el motivo no está en el catálogo del front.
 * @param {object|null|undefined} lastDisconnect
 * @returns {null | {reasonKey: string, shortKey: string|null, labelKey: string|null, fallbackLabel: string|null, statusCode: number|null, severity: "error"|"warning"|"info", at: Date|null, ongoing: boolean}}
 */
export function describeDisconnect(lastDisconnect) {
  if (!lastDisconnect || typeof lastDisconnect !== "object") return null;
  const reasonKey = typeof lastDisconnect.reason_key === "string" ? lastDisconnect.reason_key : "unknown";
  const known = REASONS[reasonKey];
  const statusCode = Number.isInteger(lastDisconnect.status_code) ? lastDisconnect.status_code : null;
  const backendSeverity = SEVERITIES.has(lastDisconnect.severity) ? lastDisconnect.severity : null;
  const backendLabel =
    typeof lastDisconnect.reason_label === "string" && lastDisconnect.reason_label.trim()
      ? lastDisconnect.reason_label.trim()
      : null;
  return {
    reasonKey,
    shortKey: known?.short ?? null,
    labelKey: known?.long ?? null,
    fallbackLabel: backendLabel,
    statusCode,
    severity: known?.severity ?? backendSeverity ?? "info",
    at: validDate(lastDisconnect.at),
    // false = the session reconnected after this disconnect (old outage).
    // Absent (older backend) counts as current.
    ongoing: lastDisconnect.ongoing !== false,
  };
}

/**
 * Texto completo del motivo, ya traducido.
 * @param {ReturnType<typeof describeDisconnect>} reason
 * @param {(key: string, opts?: object) => string} t
 */
export function reasonText(reason, t) {
  if (!reason) return "";
  if (reason.labelKey) return t(reason.labelKey);
  if (reason.fallbackLabel) return reason.fallbackLabel;
  return reason.statusCode !== null
    ? t("Desconocido (código {{code}})", { code: reason.statusCode })
    : t("Desconocido");
}

/** Texto corto del motivo (rail). null si no hay uno específico. */
export function reasonShortText(reason, t) {
  return reason?.shortKey ? t(reason.shortKey) : null;
}

// Estados en los que la sesión está caída y tiene sentido explicar por qué.
const DOWN_STATES = new Set(["close", "closed", "disconnected", "refused", "inactive", "degraded", "error"]);

/** ¿El estado de la sesión es una caída (no "conectado" ni "conectando")? */
export function isSessionDown(status) {
  return DOWN_STATES.has(String(status || "").trim().toLowerCase());
}

const INTL_LOCALE = { es: "es-CL", en: "en-US", fr: "fr-FR", pt: "pt-BR", zh: "zh-CN" };

/**
 * Fecha y hora cortas en el idioma de la interfaz, p. ej. "02-nov., 21:32";
 * `compact` usa mes numérico ("02/11, 21:32") para el ancho del rail.
 * @param {Date|string|number|null} value
 * @param {string} [language] i18n.language
 * @param {{compact?: boolean}} [opts]
 */
export function formatDisconnectTime(value, language, opts = {}) {
  const date = validDate(value);
  if (!date) return "";
  const base = String(language || "es").slice(0, 2);
  try {
    return new Intl.DateTimeFormat(INTL_LOCALE[base] || INTL_LOCALE.es, {
      day: "2-digit",
      month: opts.compact ? "2-digit" : "short",
      hour: "2-digit",
      minute: "2-digit",
      // 24 h outside English: es-CL defaults to "09:32 p. m.", which wraps
      // the rail line and reads worse in an ops tool.
      ...(base === "en" ? {} : { hourCycle: "h23" }),
    }).format(date);
  } catch {
    return date.toISOString();
  }
}

/** Etiqueta (clave i18n) del estado de un evento del historial. */
export const HISTORY_STATE_LABELS = {
  open: "Se conectó",
  connecting: "Conectando…",
  close: "Se desconectó",
  refused: "QR rechazado",
};
